import { axiosInstance } from "@/lib/axios-instance";
import { getErrorMessage } from "@/lib/api-error";

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  tokenType: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  tokens?: TokenResponse;
}

interface Workspace {
  _id: string;
  opportunity_name: string;
  created_on: string;
  deal_count?: number;
  role?: string;
  affiliated_company?: {
    _id: string;
    company_name: string;
  } | null;
  statuses: {
    colour: string;
    title: string;
    description: string;
    order: number;
  }[];
}

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await axiosInstance.post<LoginResponse>("/auth/login", {
        username: email,
        password,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Login failed");
      }

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Invalid credentials"));
    }
  },
  getWorkspaces: async (): Promise<Workspace[]> => {
    console.log("🔍 Auth API: Getting workspaces...");
    // For Clerk authentication, we don't need to pass user ID in params
    // The backend will identify the user from the Clerk session
    const response = await axiosInstance.get<{
      status: string;
      data: Workspace[];
    }>("/fetch_opportunities", {
      // Every page waits on this, so fail and say so rather than hang.
      timeout: 20000,
    });

    console.log("🔍 Auth API: Workspaces response:", response.data);

    if (response.data.status !== "success") {
      console.error("❌ Auth API: Failed to fetch workspaces:", response.data);
      throw new Error("Failed to fetch workspaces");
    }

    console.log("🔍 Auth API: Returning workspaces:", response.data.data);
    return response.data.data || [];
  },
  logout: async (refreshToken: string | null): Promise<void> => {
    try {
      await axiosInstance.post("/auth/logout", {
        refreshToken,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to logout"));
    }
  },

  refreshToken: async (
    refreshToken: string
  ): Promise<{ tokens: TokenResponse }> => {
    const response = await axiosInstance.post<{
      success: boolean;
      message: string;
      tokens: TokenResponse;
    }>("/auth/refresh", {
      refreshToken,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to refresh token");
    }

    return { tokens: response.data.tokens };
  },
  forgotPassword: async (email: string): Promise<void> => {
    const response = await axiosInstance.post("/forgot_pass", {
      email,
    });

    return response.data;
  },
  resetPassword: async (
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<void> => {
    const response = await axiosInstance.post(
      `/submit_new_pass_after_reset?token=${token}`,
      {
        new_password: password,
        new_password_again: confirmPassword,
      }
    );

    return response.data;
  },
  getInviteInfo: async (
    refId: string
  ): Promise<{
    success: boolean;
    company: {
      companyName: string;
      invitationStatus: string;
    };
  }> => {
    const response = await axiosInstance.get(`/invite/${refId}`);
    return response.data;
  },
  acceptFounderInvitation: async (
    invitationCode: string,
    firstName: string,
    lastName: string,
    email: string
  ): Promise<{
    status: string;
    message: string;
    result?: {
      user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
      };
      company: {
        id: string;
        name: string;
      };
    };
  }> => {
    const response = await axiosInstance.post("/accept-founder-invitation", {
      invitationCode,
      firstName,
      lastName,
      email,
    });

    return response.data;
  },
};

// interface CreateSandboxRequest {
//   email: string;
//   firstName: string;
//   lastName: string;
//   password: string;
//   referralSource: string;
//   otherReferralSource?: string;
// }

interface CreateCompanySandboxRequest {
  companyName: string;
  workspaceName: string;
  workspaceType: string;
  email: string;
  firstName: string;
  lastName: string;
  referralSource: string;
  otherReferralSource?: string;
}

// interface CreateSandboxUserResponse {
//   status: string;
//   message: string;
//   email?: string;
// }

// interface ResendOtpResponse {
//   status: string;
//   message: string;
//   email?: string;
// }

// interface VerifyOtpResponse {
//   status: string;
//   message: string;
//   data?: {
//     _id: string;
//     email: string;
//     first_name: string;
//     last_name: string;
//   };
// }

interface CreateCompanySandboxResponse {
  status: string;
  message?: string;
  error?: string;
  result?: {
    company: {
      _id: string;
      name: string;
    };
    workspace: {
      _id: string;
      name: string;
    };
    user: {
      _id: string;
      email: string;
      first_name: string;
      last_name: string;
    };
  };
}

export const sandboxApi = {
  // createSandboxUser: async (
  //   request: CreateSandboxRequest
  // ): Promise<CreateSandboxUserResponse> => {
  //   const response = await axiosInstance.post("/create_user_with_otp", {
  //     email: request.email,
  //     first_name: request.firstName,
  //     last_name: request.lastName,
  //     password: request.password,
  //     referral_source:
  //       request.referralSource === "other"
  //         ? request.otherReferralSource
  //         : request.referralSource,
  //   });

  //   return response.data;
  // },
  // resendOtp: async (email: string): Promise<ResendOtpResponse> => {
  //   const response = await axiosInstance.post("/resend_otp", {
  //     email,
  //   });

  //   return response.data;
  // },
  // verifyOtp: async (email: string, otp: string): Promise<VerifyOtpResponse> => {
  //   const response = await axiosInstance.post("/verify_otp", {
  //     email,
  //     otp,
  //   });

  //   return response.data;
  // },
  createCompanySandbox: async (
    request: CreateCompanySandboxRequest
  ): Promise<CreateCompanySandboxResponse> => {
    const response = await axiosInstance.post("/create_company_sandbox", {
      company_name: request.companyName,
      workspace_name: request.workspaceName,
      workspace_type: request.workspaceType,
      email: request.email,
      first_name: request.firstName,
      last_name: request.lastName,
      referral_source:
        request.referralSource === "other"
          ? request.otherReferralSource
          : request.referralSource,
    });

    return response.data;
  },
};
